import { notification } from "antd";
import { useEffect, useState } from "react";

import { generalErrorAlert } from "util/Utils";

import { TextMessage, Trove } from "../types";
import {
  getSmsTroveId,
  getTroveParams,
  getTroveTextMessages,
  makeManualTrovePayment,
  setTroveParameters,
  toCents,
} from "../utils";

export interface UseTroveManagement {
  troveId: string | null;
  trove: Trove | null;
  texts: TextMessage[];
  loading: boolean;
  saving: boolean;
  paying: boolean;
  updateTroveField: (field: keyof Trove, value: boolean | number) => void;
  saveParams: () => Promise<void>;
  makeRefill: (dollars: number) => Promise<boolean>;
}

// Owns the Trove data (params + message history) and the actions against it, keeping the page
// component and the section components purely presentational.
export const useTroveManagement = (customerId?: string): UseTroveManagement => {
  const [troveId, setTroveId] = useState<string | null>(null);
  const [trove, setTrove] = useState<Trove | null>(null);
  const [texts, setTexts] = useState<TextMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (!customerId) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const smsTrove = await getSmsTroveId(customerId);
        if (cancelled) return;
        setTroveId(smsTrove);
        if (!smsTrove) {
          setTrove(null);
          setTexts([]);
          return;
        }
        const [params, messages] = await Promise.all([getTroveParams(smsTrove), getTroveTextMessages(smsTrove)]);
        if (cancelled) return;
        setTrove(params);
        setTexts(messages);
      } catch (error) {
        if (!cancelled) {
          generalErrorAlert(error, "Trouble loading text message settings. Please refresh and try again.", true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateTroveField = (field: keyof Trove, value: boolean | number) =>
    setTrove((current) => (current ? ({ ...current, [field]: value } as Trove) : current));

  const saveParams = async () => {
    if (!trove) return;
    setSaving(true);
    try {
      await setTroveParameters(trove);
      notification.success({ message: "Text message payment preferences updated." });
    } catch (error) {
      generalErrorAlert(error, "Could not update preferences. Please try again.", true);
    } finally {
      setSaving(false);
    }
  };

  // Takes a dollar amount; converts to integer cents (the endpoint asserts int). Returns whether
  // the payment was submitted so the caller can reset its input.
  const makeRefill = async (dollars: number): Promise<boolean> => {
    if (!troveId || !customerId) return false;
    setPaying(true);
    try {
      const res = await makeManualTrovePayment(customerId, troveId, toCents(dollars));
      if (res.data?.status === "success") {
        notification.success({
          message: "Payment submitted. Your balance will update once the payment finishes processing.",
        });
        return true;
      }
      notification.error({ message: "Could not make the payment. Your primary payment method may not be set up." });
      return false;
    } catch (error) {
      generalErrorAlert(error, "Could not make the payment. Your primary payment method may not be set up.", true);
      return false;
    } finally {
      setPaying(false);
    }
  };

  return { troveId, trove, texts, loading, saving, paying, updateTroveField, saveParams, makeRefill };
};
